<template>
	<v-menu v-model="isFilterOpen" :offset="4" :close-on-content-click="false" max-width="300px" content-class="menu-content">
		<template #activator="{ props }">
			<slot name="button" :props="props"> View </slot>
		</template>
		<v-card class="menu-card search-filter" rounded="lg" elevation="8" @click.stop>
			<v-list>
				<v-list-item
					v-for="contentType in sortedContentTypes"
					:key="contentType"
					class="w-100"
					:class="{ 'selected-row': isSelected(contentType) }"
					@click.stop="toggleSelected(contentType)">
					<template v-slot:prepend>
						<v-list-item-action start>
							<v-checkbox-btn :model-value="isSelected(contentType)"></v-checkbox-btn>
						</v-list-item-action>
					</template>
					<v-list-item-title>
						{{ ContentFilterMapping[contentType] }}
					</v-list-item-title>
				</v-list-item>
			</v-list>
			<div class="opacity-50">
				<hr />
			</div>
			<v-card-actions class="d-flex justify-end">
				<v-btn
					color="grey"
					text
					rounded="xl"
					elevation="0"
					class="white-bg"
					@click="clear(() => emit('filter-content-type', []), true)"
					>Clear</v-btn
				>
				<PrimaryBtn
					@click="
						applyFilter(() =>
							emit(
								'filter-content-type',
								appliedItems.map((i) => i.id)
							)
						)
					"
					>Apply</PrimaryBtn
				>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script lang="ts" setup>
	import { computed, onMounted, ref, watch } from "vue";
	import { ContentFilterMapping } from "../../types/search/ContentFilterType";
	import { IndexableEntityType } from "@/types/search/IndexableEntityTypes";
	import { sortContentTypesBySelected } from "@/helpers/filters";
	import { useFilterMenu } from "@/composables/useFilterMenu";

	// ContentTypeFilter items are plain strings, not objects with an `id` field, so we wrap
	// them in a thin adapter that satisfies the { id: string } constraint of useFilterMenu.
	type ContentTypeItem = { id: IndexableEntityType };

	function toItem(ct: IndexableEntityType): ContentTypeItem {
		return { id: ct };
	}

	const contentTypes = ref<IndexableEntityType[]>([]);

	const props = defineProps<{
		preselectedContentTypes: IndexableEntityType[];
		parentType?: IndexableEntityType | null;
	}>();

	const emit = defineEmits<{
		(e: "filter-content-type", contentTypes: IndexableEntityType[]): void;
	}>();

	const {
		isFilterOpen,
		selectedItems,
		appliedItems,
		isSelected: isSelectedItem,
		toggleSelected: toggleSelectedItem,
		applyFilter,
		clear
	} = useFilterMenu<ContentTypeItem>();

	// Wrap string-based helpers to keep the template clean
	const isSelected = (ct: string) => isSelectedItem(toItem(ct as IndexableEntityType));
	const toggleSelected = (ct: string) => toggleSelectedItem(toItem(ct as IndexableEntityType));

	defineExpose({
		clear: (emitEvent: boolean = false) => clear(() => emit("filter-content-type", []), emitEvent),
		isFilterOpen
	});

	onMounted(async () => {
		if (props.parentType === "conversation" || props.parentType === "project") {
			// LSP-16659: the Session and Project media grids hold documents alongside photos and videos, so
			// their View filter offers Documents too. Entity types (sessions, projects, contacts) stay global-only.
			contentTypes.value = (Object.keys(ContentFilterMapping) as IndexableEntityType[]).filter(
				(x) => x === "snapshot" || x === "recording" || x === "document"
			);
		} else {
			contentTypes.value = Object.keys(ContentFilterMapping) as IndexableEntityType[];
		}

		if (props.preselectedContentTypes) {
			selectedItems.value = props.preselectedContentTypes.map(toItem);
			appliedItems.value = props.preselectedContentTypes.map(toItem);
		}
	});

	watch(
		() => props.preselectedContentTypes,
		(newVal) => {
			if (newVal) {
				selectedItems.value = newVal.map(toItem);
				appliedItems.value = newVal.map(toItem);
			} else {
				selectedItems.value = [];
				appliedItems.value = [];
			}
		}
	);

	const sortedContentTypes = computed(() => {
		return sortContentTypesBySelected(
			contentTypes.value,
			appliedItems.value.map((i) => i.id)
		);
	});
</script>

<style scoped lang="scss">
	.menu-content {
		width: 100%;
		max-width: 300px;
	}

	.menu-card {
		width: 100%;
		box-sizing: border-box;
	}
</style>
